import type { ManagedMediaDomainId, ManagedMediaSourceType, ManagedMediaStatus } from '../types';

export const MANAGED_MEDIA_DELETED_SENTINEL = '__deleted__';
export const PUBLIC_ASSETS_URL_SEGMENT = '/storage/v1/object/public/public_assets/';
export const MANAGED_PUBLIC_ASSET_PROXY_PATH = '/media/';

export type ManagedStaticMediaEntry = {
    id: string;
    domain: ManagedMediaDomainId;
    sourceType: Extract<ManagedMediaSourceType, 'static_manifest' | 'email_brand_asset'>;
    assetKind?: 'image' | 'document';
    label: string;
    description?: string;
    settingKey?: string;
    uploadPath: string;
    pathAliases?: string[];
    required: boolean;
    allowDelete: boolean;
    allowReplace?: boolean;
    references: string[];
    sortOrder: number;
};

export type ManagedTranslationImageEntry = {
    id: string;
    domain: ManagedMediaDomainId;
    label: string;
    description?: string;
    translationKey: string;
    uploadPath: string;
    references: string[];
    sortOrder: number;
};

export const MANAGED_MEDIA_DOMAIN_LABELS: Record<ManagedMediaDomainId, string> = {
    pages: 'News / Blog',
    careers: 'Careers',
    'brand-shell': 'Brand & Shell',
    'site-metadata': 'Site Metadata',
    'partner-logos': 'Partner Logos',
    audit: 'Audit / Unassigned',
};

export const MANAGED_PUBLIC_ASSET_SETTINGS = {
    brandPrimaryLogo: 'brand_primary_logo_url',
    brandEmailLogo: 'brand_email_logo_url',
    brandHeroFeather: 'brand_hero_feather_url',
    aboutTeamImage: 'about_team_image_url',
    servicesOverviewImage: 'services_overview_image_url',
    careersCultureImage: 'careers_culture_image_url',
    siteFavicon: 'site_favicon_url',
    siteAppleTouchIcon: 'site_apple_touch_icon_url',
    siteSocialShareImage: 'site_social_share_image_url',
} as const;

export const STATIC_MANAGED_MEDIA_ENTRIES: ManagedStaticMediaEntry[] = [
    {
        id: 'brand-primary-logo',
        domain: 'brand-shell',
        sourceType: 'static_manifest',
        label: 'Primary brand logo',
        description: 'Header, loading state, order access, translator portal, schema markup, email previews, and sent emails.',
        settingKey: MANAGED_PUBLIC_ASSET_SETTINGS.brandPrimaryLogo,
        uploadPath: 'brand/lm-logo',
        required: true,
        allowDelete: false,
        references: ['Header', 'Loading screen', 'Order access', 'Translator portal', 'Schema markup', 'Email previews', 'Sent emails'],
        sortOrder: 10,
    },
    {
        id: 'brand-email-logo',
        domain: 'brand-shell',
        sourceType: 'static_manifest',
        label: 'Email logo (PNG/JPEG)',
        description: 'Logo embedded in outgoing emails. Must be PNG or JPEG — WebP is not supported by most email clients. Upload a PNG export of the primary logo here.',
        settingKey: MANAGED_PUBLIC_ASSET_SETTINGS.brandEmailLogo,
        uploadPath: 'brand/lm-logo-email',
        required: false,
        allowDelete: true,
        references: ['Sent emails'],
        sortOrder: 15,
    },
    {
        id: 'brand-hero-feather',
        domain: 'brand-shell',
        sourceType: 'static_manifest',
        label: 'Hero feather background',
        description: 'Homepage hero decorative background image.',
        settingKey: MANAGED_PUBLIC_ASSET_SETTINGS.brandHeroFeather,
        uploadPath: 'brand/hero-feather',
        required: false,
        allowDelete: true,
        references: ['Homepage hero'],
        sortOrder: 20,
    },
    {
        id: 'about-team-image',
        domain: 'brand-shell',
        sourceType: 'static_manifest',
        label: 'About team image',
        description: 'Main About page image, stored with the other shared brand assets.',
        settingKey: MANAGED_PUBLIC_ASSET_SETTINGS.aboutTeamImage,
        uploadPath: 'brand/about-team-image',
        required: false,
        allowDelete: true,
        references: ['About page'],
        sortOrder: 30,
    },
    {
        id: 'services-overview-image',
        domain: 'brand-shell',
        sourceType: 'static_manifest',
        label: 'Services page overview image',
        description: 'Marketing image on the main Services page.',
        settingKey: MANAGED_PUBLIC_ASSET_SETTINGS.servicesOverviewImage,
        uploadPath: 'brand/services-overview-image',
        required: false,
        allowDelete: true,
        references: ['Services page'],
        sortOrder: 40,
    },
    {
        id: 'careers-culture-image',
        domain: 'careers',
        sourceType: 'static_manifest',
        label: 'Careers page culture image',
        description: 'Marketing image in the careers culture section.',
        settingKey: MANAGED_PUBLIC_ASSET_SETTINGS.careersCultureImage,
        uploadPath: 'careers/culture-block-image',
        required: false,
        allowDelete: true,
        references: ['Careers page'],
        sortOrder: 50,
    },
    {
        id: 'site-favicon',
        domain: 'site-metadata',
        sourceType: 'static_manifest',
        label: 'Site favicon',
        description: 'Browser tab and bookmark icon. Supports SVG, PNG, or ICO uploads.',
        settingKey: MANAGED_PUBLIC_ASSET_SETTINGS.siteFavicon,
        uploadPath: 'site/favicon',
        pathAliases: ['site/favicon', 'favicon', 'favicon.svg', 'favicon.ico', 'favicon.png'],
        required: false,
        allowDelete: true,
        references: ['Browser tabs', 'Bookmarks', 'Pinned shortcuts'],
        sortOrder: 60,
    },
    {
        id: 'site-apple-touch-icon',
        domain: 'site-metadata',
        sourceType: 'static_manifest',
        label: 'Apple touch icon',
        description: 'Home screen icon for iOS / iPadOS web shortcuts.',
        settingKey: MANAGED_PUBLIC_ASSET_SETTINGS.siteAppleTouchIcon,
        uploadPath: 'site/apple-touch-icon',
        pathAliases: ['site/apple-touch-icon', 'apple-touch-icon.png'],
        required: false,
        allowDelete: true,
        references: ['iOS home screen shortcuts'],
        sortOrder: 70,
    },
    {
        id: 'site-social-share-image',
        domain: 'site-metadata',
        sourceType: 'static_manifest',
        label: 'Default social share image',
        description: 'Fallback Open Graph and Twitter card image used when a page does not define its own share image.',
        settingKey: MANAGED_PUBLIC_ASSET_SETTINGS.siteSocialShareImage,
        uploadPath: 'site/social-share-image',
        pathAliases: ['site/social-share-image', 'og-image', 'og-image.jpg', 'og-image.png'],
        required: false,
        allowDelete: true,
        references: ['Open Graph', 'Twitter cards', 'Messaging link previews'],
        sortOrder: 80,
    },
    {
        id: 'site-sitemap-xml',
        domain: 'site-metadata',
        sourceType: 'static_manifest',
        assetKind: 'document',
        label: 'Generated sitemap.xml',
        description: 'Storage-backed sitemap used by the public /sitemap.xml pointer file.',
        uploadPath: 'sitemap.xml',
        pathAliases: ['sitemap.xml'],
        required: true,
        allowDelete: false,
        allowReplace: false,
        references: ['Search engine discovery', 'XML sitemap endpoint'],
        sortOrder: 90,
    },
];

const partnerLogoEntry = (
    id: string,
    label: string,
    sortOrder: number,
): ManagedStaticMediaEntry => ({
    id: `partner-logo-${id}`,
    domain: 'partner-logos',
    sourceType: 'static_manifest',
    label,
    settingKey: `partner_logo_${id}_url`,
    uploadPath: `partner-logos/${id}`,
    pathAliases: [`partner-logos/${id}`, `partner-logos/${id}-logo`],
    required: false,
    allowDelete: true,
    references: ['Trusted by carousel'],
    sortOrder,
});

export const PARTNER_LOGO_MANAGED_MEDIA_ENTRIES: ManagedStaticMediaEntry[] = [
    partnerLogoEntry('kaunas-court', 'Kaunas Court', 10),
    partnerLogoEntry('businesslink', 'Business Link', 20),
    partnerLogoEntry('gintarinevaistine', 'Gintarine Vaistine', 30),
    partnerLogoEntry('porterwood', 'Porterwood', 40),
    partnerLogoEntry('ktu', 'KTU', 50),
    partnerLogoEntry('meteo', 'Meteo', 60),
    partnerLogoEntry('teismo', 'Teismo', 70),
    partnerLogoEntry('carreblanc', 'Carre Blanc', 80),
    partnerLogoEntry('aecom', 'AECOM', 90),
    partnerLogoEntry('lsmu', 'LSMU', 100),
    partnerLogoEntry('arbormedical', 'Arbor Medical', 110),
    partnerLogoEntry('aviabaltika', 'Aviabaltika', 120),
    partnerLogoEntry('brasta', 'Brasta', 130),
    partnerLogoEntry('deigas', 'Deigas', 140),
    partnerLogoEntry('iceco', 'ICECO', 150),
    partnerLogoEntry('ilk', 'ILK', 160),
    partnerLogoEntry('kautra', 'Kautra', 170),
    partnerLogoEntry('kavosbankas', 'Kavos Bankas', 180),
    partnerLogoEntry('klingai', 'Klingai', 190),
    partnerLogoEntry('medziostilius', 'Medzio Stilius', 200),
    partnerLogoEntry('puseplus', 'Puse Plus', 210),
    partnerLogoEntry('vilpak', 'Vilpak', 220),
];

export const MANAGED_TRANSLATION_IMAGE_ENTRIES: ManagedTranslationImageEntry[] = [
    {
        id: 'careers-testimonial-t1',
        domain: 'careers',
        label: 'Career testimonial 1',
        translationKey: 'nav.careersPage.testimonials.items.t1.image',
        uploadPath: 'careers/testimonial-t1',
        references: ['Careers testimonials carousel'],
        sortOrder: 10,
    },
    {
        id: 'careers-testimonial-t2',
        domain: 'careers',
        label: 'Career testimonial 2',
        translationKey: 'nav.careersPage.testimonials.items.t2.image',
        uploadPath: 'careers/testimonial-t2',
        references: ['Careers testimonials carousel'],
        sortOrder: 20,
    },
    {
        id: 'careers-testimonial-t3',
        domain: 'careers',
        label: 'Career testimonial 3',
        translationKey: 'nav.careersPage.testimonials.items.t3.image',
        uploadPath: 'careers/testimonial-t3',
        references: ['Careers testimonials carousel'],
        sortOrder: 30,
    },
    {
        id: 'careers-testimonial-t4',
        domain: 'careers',
        label: 'Career testimonial 4',
        translationKey: 'nav.careersPage.testimonials.items.t4.image',
        uploadPath: 'careers/testimonial-t4',
        references: ['Careers testimonials carousel'],
        sortOrder: 40,
    },
];

export const resolveManagedSettingUrl = (
    settings: Record<string, string> | null | undefined,
    settingKey: string,
) => {
    const rawValue = String(settings?.[settingKey] || '').trim();
    if (rawValue === MANAGED_MEDIA_DELETED_SENTINEL) return null;
    const storagePath = extractPublicAssetPath(rawValue);
    return storagePath ? buildManagedPublicAssetClientUrl(storagePath, rawValue) : null;
};

export const isPublicAssetUrl = (value?: string | null) => Boolean(extractPublicAssetPath(value));

const sanitizeManagedPathSegment = (value: string) =>
    String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9.-]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '');

export const normalizeManagedSectionSlotId = (sectionId: string) =>
    sanitizeManagedPathSegment(String(sectionId || '').trim().toLowerCase().replace(/(?:-lt|-en)(?=-|$)/g, '')) || 'section';

export const extractPublicAssetPath = (value?: string | null) => {
    const url = String(value || '').trim();
    if (!url) return null;
    const markerIndex = url.indexOf(PUBLIC_ASSETS_URL_SEGMENT);
    if (markerIndex >= 0) {
        const pathWithQuery = url.slice(markerIndex + PUBLIC_ASSETS_URL_SEGMENT.length);
        return pathWithQuery.split('?')[0] || null;
    }
    return null;
};

export const buildManagedPublicAssetUrl = (projectUrl: string, storagePath?: string | null) => {
    const normalizedProjectUrl = String(projectUrl || '').trim().replace(/\/+$/, '');
    const normalizedStoragePath = String(storagePath || '').trim().replace(/^\/+/, '');
    if (!normalizedProjectUrl || !normalizedStoragePath) return null;
    return `${normalizedProjectUrl}/storage/v1/object/public/public_assets/${normalizedStoragePath}`;
};

export const buildManagedPublicAssetClientUrl = (storagePath?: string | null, fallbackUrl?: string | null) => {
    const normalizedStoragePath = String(storagePath || '').trim().replace(/^\/+/, '');
    if (!normalizedStoragePath) return fallbackUrl ? String(fallbackUrl).trim() || null : null;

    if (typeof window !== 'undefined') {
        const origin = String(window.location.origin || '').trim().replace(/\/+$/, '');
        if (origin) {
            return `${origin}${MANAGED_PUBLIC_ASSET_PROXY_PATH}${normalizedStoragePath}`;
        }
    }

    return fallbackUrl ? String(fallbackUrl).trim() || null : null;
};

export const getManagedSlotStatus = (
    existsInStorage: boolean,
    options: { unassigned?: boolean } = {},
): ManagedMediaStatus => {
    if (existsInStorage) return 'ready';
    if (options.unassigned) return 'unassigned';
    return 'missing_storage';
};

export const isManagedReferenceNormalized = (
    value: string | null | undefined,
    expectedPublicUrl?: string | null,
    expectedStoragePath?: string | null,
) => {
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) return false;

    const normalizedExpectedUrl = String(expectedPublicUrl || '').trim();
    if (normalizedExpectedUrl && normalizedValue === normalizedExpectedUrl) return true;

    const currentStoragePath = extractPublicAssetPath(normalizedValue);
    const normalizedExpectedPath = String(expectedStoragePath || '').trim();
    return Boolean(currentStoragePath && normalizedExpectedPath && currentStoragePath === normalizedExpectedPath);
};

export const buildManagedContentSectionItemId = (pageId: string, sectionId: string) =>
    `content-section:${String(pageId).trim()}:${normalizeManagedSectionSlotId(sectionId)}`;

export const parseManagedContentSectionItemId = (itemId: string) => {
    const match = String(itemId || '').trim().match(/^content-section:([^:]+):(.+)$/i);
    if (!match) return null;
    return {
        pageId: match[1],
        sectionId: match[2],
    };
};

export const buildManagedContentSectionUploadPath = (
    pageDomain: ManagedMediaDomainId,
    pageKey: string | null | undefined,
    pageId: string,
    sectionId: string,
) => {
    const pageSegment = sanitizeManagedPathSegment(pageKey || pageId) || sanitizeManagedPathSegment(pageId) || 'page';
    const sectionSegment = normalizeManagedSectionSlotId(sectionId);
    return `${pageDomain}/${pageSegment}-${sectionSegment}`;
};

export const getManagedMediaEntryById = (itemId: string) =>
    [...STATIC_MANAGED_MEDIA_ENTRIES, ...PARTNER_LOGO_MANAGED_MEDIA_ENTRIES].find((entry) => entry.id === itemId) || null;

export const getManagedMediaEntryBySettingKey = (settingKey: string) =>
    [...STATIC_MANAGED_MEDIA_ENTRIES, ...PARTNER_LOGO_MANAGED_MEDIA_ENTRIES].find((entry) => entry.settingKey === settingKey) || null;

export const getManagedTranslationEntryById = (itemId: string) =>
    MANAGED_TRANSLATION_IMAGE_ENTRIES.find((entry) => entry.id === itemId) || null;

export const getManagedTranslationEntryByKey = (translationKey: string) =>
    MANAGED_TRANSLATION_IMAGE_ENTRIES.find((entry) => entry.translationKey === translationKey) || null;
